<template>
  <base-page-layout title="Dashboard" :filters="['data', 'lojas', 'segmentos']">
    <template v-slot:header>
      <div class="mr-2">
        <dashboard-pdf :loading="loading" :dashboard-cards-metrics="dashboardCardsMetrics" :filters="stateFilters" />
      </div>
    </template>
    <v-container fluid grid-list-md class="px-0 pt-0">
      <v-layout row wrap>
        <v-flex v-for="(card, index) in cards" :key="index" xs12 sm6 md4>
          <cbm-card
            :number-text="card.value"
            :title="card.text"
            :tooltip="card.tooltipText"
            :percent-description="card.description"
            :percent-text="card.underText"
            :is-percent="card.isPercent"
            :is-currency="card.isCurrency"
            :is-integer="card.isInteger"
            :boolean-loader="loading"
            :quick-action="card.quickAction"
            simple-card
            unset-percentage-colors
          />
        </v-flex>
      </v-layout>
    </v-container>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import CbmCard from '@/components/CbmCard.vue'
import { localeString } from '@/config/helpers.js'
import { criteriaGroup, anyPurchaseCriteria, signupDateCriteria, isLoyalCriteria } from '@/components/quick-actions/segmentationGroups.js'

import { mapGetters, mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'

export default {
  components: {
    BasePageLayout,
    CbmCard,
    DashboardPdf: () => import('@/modules/clube/dashboard/views/components/pdf/DashboardPdf.vue'),
  },
  data: () => ({
    loading: true,
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    ...mapGetters({
      totalClientesCadastrados: 'clubeClientesCadastradosTotal',
      clientesCadastrados: 'clubeClientesCadastrados',
      clientesAtivos: 'clubeClientesAtivos',
      percentualFaturamentoFidelidade: 'clubePercentualFaturamentoFidelidade',
      ticketMedioFidelidade: 'clubeTicketMedioFidelidade',
      ticketMedio: 'clubeTicketMedio',
      percentualTicketMedio: 'clubePercentualTicketMedio',
      mediaCompras: 'clubeMediaCompras',
      gastoMedio: 'clubeGastoMedio',
      mediaItens: 'clubeMediaItens',
      cuponsFidelidade: 'clubeCuponsFidelidade',
      faturamentoFidelidade: 'clubeFaturamentoFidelidade',
      cuponsFora: 'clubeCuponsFora',
      faturamentoFora: 'clubeFaturamentoFora',
    }),
    porcentagemClientesAtivos() {
      const porcentagem = (this.clientesAtivos / this.totalClientesCadastrados) * 100
      return localeString(porcentagem, 1) + '%'
    },
    cards() {
      return [
        {
          value: localeString(this.clientesCadastrados || 0),
          text: this.$t('clube.novos_cadastros_clube'),
          quickAction: {
            id: 'novos-cadastros',
            shortDescription: `Novos cadastros fidelidade`,
            longDescription: (formatters) => `Novos cadastros fidelidade - ${formatters.startDate} a ${formatters.endDate}`,
            segmentationGroup: (quickActionFiltersCriteria) =>
              criteriaGroup([
                signupDateCriteria(...quickActionFiltersCriteria.dateCriteriaFilter),
                isLoyalCriteria(true),
                ...quickActionFiltersCriteria.criteria.segments,
              ]),
          },
        },
        {
          value: localeString(this.clientesAtivos || 0),
          text: this.$t('clube.clientes_ativos_clube'),
          underText: localeString(this.porcentagemClientesAtivos, 1),
          description: `da base de ${localeString(this.totalClientesCadastrados, 0)} clientes`,
          quickAction: {
            id: 'clientes-ativos',
            shortDescription: `Clientes ativos fidelidade`,
            longDescription: (formatters) => `Clientes ativos fidelidade- ${formatters.startDate} a ${formatters.endDate}`,
            segmentationGroup: (quickActionFiltersCriteria) =>
              criteriaGroup([
                anyPurchaseCriteria(...quickActionFiltersCriteria.dateCriteriaFilter),
                isLoyalCriteria(true),
                ...quickActionFiltersCriteria.criteria.segments,
                ...quickActionFiltersCriteria.criteria.units,
              ]),
          },
        },
        {
          value: localeString(this.percentualFaturamentoFidelidade || 0, 1),
          text: this.$t('clube.percentual_faturamento_clube'),
          isPercent: true,
        },
        {
          value: localeString(this.ticketMedioFidelidade || 0, 2),
          text: this.$t('clube.ticket_medio_clube'),
          isCurrency: true,
          underText: `R$ ${localeString(this.faturamentoFidelidade, 2)}`,
          description: `/ ${localeString(this.cuponsFidelidade, 0)} cupons`,
        },
        {
          value: localeString(this.ticketMedio || 0, 2),
          text: this.$t('clube.ticket_medio_fora_clube'),
          isCurrency: true,
          underText: `R$ ${localeString(this.faturamentoFora, 2)}`,
          description: `/ ${localeString(this.cuponsFora, 0)} cupons`,
        },
        {
          value: localeString(this.percentualTicketMedio || 0, 1),
          text: this.$t('clube.variacao_ticket_medio'),
          tooltipText: this.$t('clube.variacao_ticket_medio_help_text'),
          isPercent: true,
        },
        {
          value: localeString(this.mediaCompras || 0, 1),
          text: this.$t('clube.media_cupons_clube'),
        },
        {
          value: localeString(this.gastoMedio || 0, 2),
          text: this.$t('clube.gasto_medio_clube'),
          isCurrency: true,
          tooltipText: this.$t('clube.gasto_medio_clube_help_text'),
          loader: 'gasto-medio',
        },
        {
          value: localeString(this.mediaItens || 0, 1),
          text: this.$t('clube.media_itens_clube'),
        },
      ]
    },
    dashboardCardsMetrics() {
      return this.cards.map((card) => ({
        title: card.text,
        numberText: card.value,
        percentText: card.underText || '',
        percentDescription: card.description || '',
        isCurrency: card.isCurrency || false,
        isPercent: card.isPercent || false,
        tooltip: card.tooltipText || '',
        unsetPercentageColors: true,
      }))
    },
  },
  async mounted() {
    await this.getData()
    this.$nextTick(() => {
      this.$bus.$on('filter', this.getData)
    })
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    ...mapWaitingActions({
      clubeClientesCadastradosTotal: 'total-clientes-cadastrados',
      clubeClientesCadastrados: 'clientes-cadastrados',
      clubeClientesAtivos: 'clientes-ativos',
      clubePercentualFaturamentoFidelidade: 'percentual-faturamento-fidelidade',
      clubeTicketMedioFidelidade: 'ticket-medio-fidelidade',
      clubeTicketMedio: 'ticket-medio',
      clubePercentualTicketMedio: 'percentual-ticket-medio',
      clubeMediaCompras: 'media-compras',
      clubeGastoMedio: 'gasto-medio',
      clubeMediaItens: 'media-itens',
      clubeCuponsFidelidade: 'cupons-fidelidade',
      clubeFaturamentoFidelidade: 'faturamento-fidelidade',
      clubeCuponsFora: 'cupons-fora',
      clubeFaturamentoFora: 'faturamento-fora',
    }),

    async getData() {
      const filter = {
        date: this.stateFilters.data,
        unidades: this.stateFilters.unitsConfiguration,
        segmentos: this.stateFilters.segments,
      }

      if (this.stateFilters.data?.[0].length > 0) {
        try {
          this.loading = true
          await Promise.all([
            this.clubePercentualTicketMedio(filter),
            this.clubeTicketMedio({ ...filter, fidelidade: '0' }),
            this.clubeCuponsFora({ ...filter, fidelidade: '0' }),
            this.clubeFaturamentoFora({ ...filter, fidelidade: '0' }),
            this.clubeClientesCadastrados({ ...filter, fidelidade: '1' }),
            this.clubeClientesAtivos({ ...filter, fidelidade: '1' }),
            this.clubePercentualFaturamentoFidelidade({ ...filter, fidelidade: '1' }),
            this.clubeTicketMedioFidelidade({ ...filter, fidelidade: '1' }),
            this.clubeMediaCompras({ ...filter, fidelidade: '1' }),
            this.clubeGastoMedio({ ...filter, fidelidade: '1' }),
            this.clubeMediaItens({ ...filter, fidelidade: '1' }),
            this.clubeCuponsFidelidade({ ...filter, fidelidade: '1' }),
            this.clubeFaturamentoFidelidade({ ...filter, fidelidade: '1' }),
            this.clubeClientesCadastradosTotal({ fidelidade: '1' }),
          ])
        } catch (error) {
          console.error(error)
        } finally {
          this.loading = false
        }
      }
    },
  },
}
</script>
